// React
import React from 'react'
import PropTypes from 'prop-types'

// Libraries
import {
  useTranslation as originalUseTranslation,
  Trans
} from 'react-i18next'

// Components
import Skeleton from 'ui/blocks/Skeleton'

/**
 * Hook to use in functional components to get the `t` function from `react-i18next`,
 * but with a skeleton fallback when the translations are not ready yet.
 *
 * @param {string} namespace The namespace to use for the translations.
 *
 * @returns {[
 *   (key: string, options?: object) => string | JSX.Element,
 *   object,
 *   boolean
 * ] & {
 *   t: (key: string, options?: object) => string | JSX.Element,
 *   tOrEmptyString: (key: string, options?: object) => string,
 *   i18n: object,
 *   Trans: React.ComponentType<{i18nKey: string}>
 * }} Array with [tOrLoadingBar, i18n, ready] and additional properties for object destructuring
 */
export const useTranslation = (namespace) => {
  const [t, i18n, ready] = originalUseTranslation(namespace, { useSuspense: false })

  const tOrLoadingBar = (key, options = {}) => {
    return ready ? t(key, options) : <Skeleton data-tid="Translation loading" key={key} namespace={namespace} ready={ready} string={key} />
  }

  const tOrEmptyString = (key, options = {}) => {
    return ready ? t(key, options) : ''
  }

  const TransComponent = ({ i18nKey, ...transProps }) => {
    return ready ?
      <Trans t={t} i18nKey={i18nKey} {...transProps} /> :
      <Skeleton data-tid="Translation loading" key={i18nKey} namespace={namespace} ready={ready} string={i18nKey} />
  }

  TransComponent.propTypes = {
    i18nKey: PropTypes.string
  }

  // Allow destructuring the return value as an array or as an object:
  //   const [t, i18n, ready] = useTranslation()
  //   or
  //   const { t, i18n, ready, ...otherProps } = useTranslation()
  //
  const ret = [tOrLoadingBar, i18n, ready]
  ret.t = tOrLoadingBar
  ret.tOrEmptyString = tOrEmptyString
  ret.i18n = i18n
  ret.ready = ready
  ret.Trans = TransComponent

  return ret
}
